/* Keeping a trace of a breach after it stops being one.
 *
 * Alerts are computed from the book as it stands, which is right — they are
 * about now. The cost is that a limit you went past and then fixed leaves
 * nothing behind: the card simply stops appearing, and whether it was ever
 * wrong becomes unanswerable a week later.
 *
 * So an episode is opened when a breach first appears and closed when it stops.
 * It records that there was one and for how long. It does not say whether
 * anything should have been done about it — that is a judgement, and the book
 * does not make those (§1). */

import type { Alert } from '../rules/engine';
import type { AlertEpisode } from '../types';

/** How stale `lastSeen` may get before it is worth another write. The rules run
    on every render; without this the log would be rewritten continuously. */
const TOUCH_MS = 60 * 60 * 1000;

export function reconcileAlertLog(
  live: Alert[],
  stored: AlertEpisode[],
  nowIso = new Date().toISOString(),
): AlertEpisode[] {
  const now = new Date(nowIso).getTime();
  const changed: AlertEpisode[] = [];

  const openByRule = new Map<string, AlertEpisode>();
  for (const e of stored) if (!e.resolvedAt) openByRule.set(e.id, e);

  const liveIds = new Set<string>();

  for (const a of live) {
    liveIds.add(a.id);
    const open = openByRule.get(a.id);

    if (!open) {
      const episode: AlertEpisode = {
        id: a.id,
        severity: a.severity,
        headline: a.headline,
        firstSeen: nowIso,
        lastSeen: nowIso,
      };
      if (a.positionId) episode.ticker = a.positionId;
      changed.push(episode);
      continue;
    }

    // Still standing. Only worth a write once the record has gone stale.
    if (now - new Date(open.lastSeen).getTime() >= TOUCH_MS) {
      changed.push({ ...open, lastSeen: nowIso, headline: a.headline });
    }
  }

  // Gone: whatever was open and is no longer raised.
  for (const [id, open] of openByRule) {
    if (!liveIds.has(id)) changed.push({ ...open, resolvedAt: nowIso });
  }

  return changed;
}
